import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  IconButton,
  Box,
  Divider,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";

// Giả định đây là file API của bạn
import supplierApi from "../api/supplierApi";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const pageNo = "InvoiceSupplierConnectionPage";
function InvoiceSupplierConnectionPage() {
  const roles = useSelector((state) => state.auth.roles) || null;

  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSupplierName, setEditingSupplierName] = useState("");
  const [editingEsHook, setEditingEsHook] = useState("");
  const [editingEsIsUseReplacePayment, setEditingEsIsUseReplacePayment] =
    useState(false);
  const [editingEsIsUseToken, setEditingEsIsUseToken] = useState(false);
  const [editingEsIsUseApiTemplate, setEditingEsIsUseApiTemplate] =
    useState(false);
  const [editingEsIsUseCertificate, setEditingEsIsUseCertificate] =
    useState(false);
  const [editingEsIsLockBillAfterSync, setEditingEsIsLockBillAfterSync] =
    useState(false);
  const [editingEsIsUseButtonSign, setEditingEsIsUseButtonSign] =
    useState(false);
  const [editingEsDescription, setEditingEsDescription] = useState("");

  const [isAddingNew, setIsAddingNew] = useState(false);

  // --- Lấy dữ liệu nhà cung cấp từ API ---
  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await supplierApi.getSupplier();
      if (response.success) {
        setSuppliers(response.data);
      } else {
        setError(response.message || "Không thể tải dữ liệu nhà cung cấp.");
      }
    } catch (err) {
      console.error("Lỗi khi gọi API:", err);
      setError("Đã xảy ra lỗi khi kết nối đến máy chủ.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleAddNewSupplierClick = () => {
    setIsAddingNew(true);
    setSelectedSupplier({
      EsId: null,
      EsName: "",
      EsHook: "",
      EsIsUseReplacePayment: false,
      EsIsUseToken: false,
      EsIsUseApiTemplate: false,
      EsExtendProperties: [],
      EsIsLockBillAfterSync: false,
      EsIsUseButtonSign: false,
      EsDescription: "",
      EsIsUseCertificate: false,
    });
    setEditingSupplierName("");
    setEditingEsHook("");
    setEditingEsIsUseReplacePayment(false);
    setEditingEsIsUseToken(false);
    setEditingEsIsUseApiTemplate(false);
    setEditingEsIsLockBillAfterSync(false);
    setEditingEsIsUseButtonSign(false);
    setEditingEsDescription("");
    setEditingEsIsUseCertificate(false);
  };

  const handleSelectSupplier = (supplier) => {
    setIsAddingNew(false);
    setSelectedSupplier({
      ...supplier,
      EsExtendProperties: Array.isArray(supplier.EsExtendProperties)
        ? supplier.EsExtendProperties
        : [],
    });
    setEditingSupplierName(supplier.EsName);
    setEditingEsHook(supplier.EsHook || "");
    setEditingEsIsUseReplacePayment(supplier.EsIsUseReplacePayment || false);
    setEditingEsIsUseToken(supplier.EsIsUseToken || false);
    setEditingEsIsUseApiTemplate(supplier.EsIsUseApiTemplate || false);
    setEditingEsIsLockBillAfterSync(supplier.EsIsLockBillAfterSync || false);
    setEditingEsIsUseButtonSign(supplier.EsIsUseButtonSign || false);
    setEditingEsDescription(supplier.EsDescription || "");
    setEditingEsIsUseCertificate(supplier.EsIsUseCertificate || false);
  };

  const handleAddProperty = () => {
    const updatedProperties = [
      ...(selectedSupplier.EsExtendProperties || []),
      {
        fieldName: "",
        parameterName: "",
        defaultValue: "",
        isRequired: false,
        isHidden: false,
      },
    ];
    setSelectedSupplier((prev) => ({
      ...prev,
      EsExtendProperties: updatedProperties,
    }));
  };

  const handleDeleteProperty = (propertyIndex) => {
    if (selectedSupplier) {
      const updatedProperties = selectedSupplier.EsExtendProperties.filter(
        (_, index) => index !== propertyIndex,
      );

      setSelectedSupplier((prev) => ({
        ...prev,
        EsExtendProperties: updatedProperties,
      }));
    }
  };

  const handlePropertyChange = (propertyIndex, field, value) => {
    if (selectedSupplier) {
      const updatedProperties = selectedSupplier.EsExtendProperties.map(
        (prop, idx) =>
          idx === propertyIndex ? { ...prop, [field]: value } : prop,
      );

      setSelectedSupplier((prev) => ({
        ...prev,
        EsExtendProperties: updatedProperties,
      }));
    }
  };

  const handleSaveChanges = async () => {
    if (editingSupplierName.trim() === "") {
      toast.error("Tên nhà cung cấp không được để trống!");
      return;
    }
    if (editingEsHook.trim() === "") {
      toast.error("Đường dẫn hook không được để trống!");
      return;
    }

    const supplierData = {
      EsName: editingSupplierName.trim(),
      EsHook: editingEsHook.trim(),
      EsIsUseReplacePayment: editingEsIsUseReplacePayment,
      EsIsUseToken: editingEsIsUseToken,
      EsIsUseApiTemplate: editingEsIsUseApiTemplate,
      EsExtendProperties: selectedSupplier?.EsExtendProperties || [],
      EsIsLockBillAfterSync: editingEsIsLockBillAfterSync,
      EsIsUseButtonSign: editingEsIsUseButtonSign,
      EsDescription: editingEsDescription.trim(),
      EsIsUseCertificate: editingEsIsUseCertificate,
    };
    // Format json sang string json
    supplierData.EsExtendProperties = JSON.stringify(
      supplierData.EsExtendProperties,
    );
    try {
      if (!isAddingNew) supplierData.EsId = selectedSupplier.EsId;
      const response = await supplierApi.upsertSupplier(supplierData);
      if (response.success) {
        toast.success("Lưu thành công!");
        setSelectedSupplier(null);
        setIsAddingNew(false);
        await fetchSuppliers();
      } else {
        toast.error(response.message || "Lưu thất bại!");
      }
    } catch (apiError) {
      console.error("Lỗi API khi lưu:", apiError);
      toast.error("Đã xảy ra lỗi khi kết nối đến máy chủ để lưu.");
    }
  };

  const handleDelete = async () => {
    if (selectedSupplier) {
      try {
        const response = await supplierApi.deleteSupplier(
          selectedSupplier.EsId,
        );
        if (response.success) {
          toast.success("Xoá thành công!");
          setSelectedSupplier(null);
          await fetchSuppliers();
        } else {
          toast.error(response.message || "Xoá thất bại!");
        }
      } catch (apiError) {
        console.error("Lỗi API khi xoá:", apiError);
        toast.error("Đã xảy ra lỗi khi kết nối đến máy chủ để xoá.");
      }
    }
  };

  return (
    <Container
      sx={{
        width: "100vw",
      }}
    >
      <Grid container spacing={3} alignItems="flex-start">
        {/* Phần bên trái: Danh sách nhà cung cấp */}
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              minHeight: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Danh sách nhà cung cấp
            </Typography>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <>
                <List sx={{ flexGrow: 1, overflowY: "auto" }}>
                  {suppliers.map((supplier) => (
                    <ListItem
                      key={supplier.EsId}
                      button={true}
                      onClick={() => handleSelectSupplier(supplier)}
                      sx={{
                        color:
                          selectedSupplier &&
                          selectedSupplier.EsId === supplier.EsId &&
                          !isAddingNew
                            ? "blue"
                            : "inherit",
                      }}
                    >
                      <ListItemText primary={supplier.EsName} />
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ mt: 2, display: "flex", gap: 1, flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddNewSupplierClick}
                    fullWidth
                    disabled={isAddingNew}
                  >
                    Thêm mới
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Phần bên phải: Chi tiết nhà cung cấp */}
        <Grid
          size={{
            xs: 12,
            md: 8,
          }}
        >
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              minHeight: 300,
            }}
          >
            {selectedSupplier || isAddingNew ? (
              <>
                <Typography variant="h6" gutterBottom>
                  {isAddingNew
                    ? "Thêm nhà cung cấp mới"
                    : `Chi tiết nhà cung cấp: ${editingSupplierName}`}
                </Typography>

                <TextField
                  label="Tên nhà cung cấp"
                  variant="outlined"
                  value={editingSupplierName}
                  onChange={(e) => setEditingSupplierName(e.target.value)}
                  fullWidth
                  sx={{ mb: 1 }}
                  size="small"
                  required
                />

                <TextField
                  label="Hook Url"
                  variant="outlined"
                  value={editingEsHook}
                  onChange={(e) => setEditingEsHook(e.target.value)}
                  fullWidth
                  sx={{ mb: 1 }}
                  size="small"
                  required
                />
                <TextField
                  label="Ghi chú"
                  variant="outlined"
                  multiline
                  rows={2}
                  fullWidth
                  value={editingEsDescription || ""}
                  onChange={(e) => setEditingEsDescription(e.target.value)}
                ></TextField>
                <Grid sx={{ display: "flex" }}>
                  <Grid
                    sx={{ display: "flex", flexDirection: "column", mb: 1 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingEsIsUseReplacePayment || false}
                          onChange={(e) => {
                            setEditingEsIsUseReplacePayment(e.target.checked);
                          }}
                        />
                      }
                      label="Sử dụng giá trị thay thế cho hình thức thanh toán"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingEsIsUseToken || false}
                          onChange={(e) => {
                            setEditingEsIsUseToken(e.target.checked);
                          }}
                        />
                      }
                      label="Sử dụng api lấy token"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingEsIsUseApiTemplate || false}
                          onChange={(e) => {
                            setEditingEsIsUseApiTemplate(e.target.checked);
                          }}
                        />
                      }
                      label="Sử dụng api lấy mẫu ký hiệu"
                    />
                  </Grid>
                  <Grid
                    sx={{ display: "flex", flexDirection: "column", mb: 1 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingEsIsUseCertificate || false}
                          onChange={(e) => {
                            setEditingEsIsUseCertificate(e.target.checked);
                          }}
                        />
                      }
                      label="Sử dụng chứng thư số"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingEsIsLockBillAfterSync || false}
                          onChange={(e) => {
                            setEditingEsIsLockBillAfterSync(e.target.checked);
                          }}
                        />
                      }
                      label="Khóa bill sau khi đồng bộ"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingEsIsUseButtonSign || false}
                          onChange={(e) => {
                            setEditingEsIsUseButtonSign(e.target.checked);
                          }}
                        />
                      }
                      label="Sử dụng nút ký"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 1 }} />
                <Typography variant="h6">Các thuộc tính mở rộng:</Typography>
                {!isAddingNew && (
                  <Typography variant="subtitle2" sx={{ mb: 1, color: "red" }}>
                    Lưu ý xoá, sửa thuộc tính có thể làm ảnh hường đến khách
                    háng đang sử dụng nhà cung cấp này.
                  </Typography>
                )}

                {selectedSupplier?.EsExtendProperties?.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Chưa có thuộc tính nào cho nhà cung cấp này.
                  </Typography>
                )}
                <Box
                  sx={{ flexGrow: 1, overflowY: "auto", paddingTop: "inherit" }}
                >
                  {(selectedSupplier?.EsExtendProperties || []).map(
                    (property, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <TextField
                          label="Tên trường (fieldName)"
                          variant="outlined"
                          size="small"
                          value={property.fieldName}
                          onChange={(e) =>
                            handlePropertyChange(
                              index,
                              "fieldName",
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Tên tham số (parameterName)"
                          variant="outlined"
                          size="small"
                          value={property.parameterName}
                          onChange={(e) =>
                            handlePropertyChange(
                              index,
                              "parameterName",
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Giá trị mặc định"
                          variant="outlined"
                          size="small"
                          value={property.defaultValue || ""}
                          onChange={(e) =>
                            handlePropertyChange(
                              index,
                              "defaultValue",
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={property.isRequired || false}
                              onChange={(e) =>
                                handlePropertyChange(
                                  index,
                                  "isRequired",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="Bắt buộc"
                          sx={{ flexShrink: 0 }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={property.isHidden || false}
                              onChange={(e) =>
                                handlePropertyChange(
                                  index,
                                  "isHidden",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="Ẩn"
                          sx={{ flexShrink: 0 }}
                        />
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteProperty(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ),
                  )}
                </Box>
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddProperty}
                  >
                    Thêm thuộc tính
                  </Button>
                  <Box>
                    {/* <Button
                      variant="contained"
                      startIcon={<DeleteIcon />}
                      onClick={handleDelete}
                      color="error"
                      disabled={!selectedSupplier?.EsId}
                    >
                      Xoá
                    </Button> */}
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveChanges}
                      color="primary"
                      sx={{ ml: 1 }}
                    >
                      Lưu
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Vui lòng chọn một nhà cung cấp hoặc nhấn "Thêm mới" để tạo nhà
                cung cấp mới.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default InvoiceSupplierConnectionPage;
